import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import classnames from 'classnames';
import cxs from 'cxs';
import { noop, debounce, startsWith, isUndefined, size, assign, chunk, range, map, clamp, indexOf } from 'lodash';
import React from 'react';
import { ContentShifter } from './content-shifter/content-shifter';
import { ContentSlider } from '../content-slider/content-slider';
import { PageList } from '../page-list/page-list';
import { NavButton } from '../nav/button/button';
import { InlineIcon } from '../inline-icon/inline-icon';
import { KeyboardNavigation } from './keyboard-navigation/keyboard-navigation';
import { SliderWrapper } from './wrapper/wrapper';
import { MainItemWatcher } from './main-item-watcher/main-item-watcher';
import { ItemLoading } from './item-loading/item-loading';
import { focus } from '../../widget';
import styles from './styles/slider.scss';
import ArrowLeftShadyIcon from '../../icons/compiled/components/ArrowLeftShadyIcon';
const round = number => Math.floor(number * 100) / 100;
const FAKE_ITEM_ID_PREFIX = 'fake';
const DEFAULT_VIDEOS_COUNT = 8;
class SliderComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "activeItemRef", void 0);
    _defineProperty(this, "containerRef", void 0);
    _defineProperty(this, "activeItemOverlayRef", void 0);
    _defineProperty(this, "loadMore", debounce(itemsCount => {
      const {
        loadMoreItems,
        canLoadMoreItems
      } = this.props;
      if (canLoadMoreItems) {
        loadMoreItems(itemsCount);
      }
    }, 300));
    _defineProperty(this, "getVisibleItemsCount", () => this.state.firstItemAtFrameIndex + this.props.inFrameItemsCount);
    _defineProperty(this, "getMissingItemsCount", () => Math.max(0, this.getVisibleItemsCount() - this.props.loadedItemsCount));
    _defineProperty(this, "isPrevButtonVisible", () => {
      if (!this.state.mounted) {
        return false;
      }
      return this.state.firstItemAtFrameIndex > 0 || this.state.isLastItemForcedInView;
    });
    _defineProperty(this, "isLastPage", () => this.getVisibleItemsCount() === this.props.itemsCount);
    _defineProperty(this, "isLastItemPartiallyVisible", () => {
      const {
        width,
        itemWidthWithSpacing,
        horizontalSpacing,
        inFrameItemsCount
      } = this.props;
      const minWidthNeeded = inFrameItemsCount * itemWidthWithSpacing - horizontalSpacing;
      return this.isLastPage() && !this.state.isLastItemForcedInView && width < minWidthNeeded;
    });
    _defineProperty(this, "isLeftArrowVisible", () => this.props.isRTL ? this.isNextButtonVisible() : this.isPrevButtonVisible());
    _defineProperty(this, "isRightArrowVisible", () => this.props.isRTL ? this.isPrevButtonVisible() : this.isNextButtonVisible());
    _defineProperty(this, "isNextButtonVisible", () => {
      if (!this.state.mounted) {
        return false;
      }
      return this.isLastItemPartiallyVisible() || this.getVisibleItemsCount() < this.props.itemsCount;
    });
    _defineProperty(this, "updateState", nextState => {
      this.setState({
        ...nextState,
        isTransitioning: true
      });
    });
    _defineProperty(this, "setFirstItemAtFrameIndex", firstItemAtFrameIndex => {
      this.updateState({
        firstItemAtFrameIndex
      });
    });
    _defineProperty(this, "setLastItemForcedInView", isLastItemForcedInView => {
      this.updateState({
        isLastItemForcedInView
      });
    });
    _defineProperty(this, "focusContainer", () => {
      // Timeout - wait for content update
      setTimeout(() => {
        focus(this.containerRef);
      }, 10);
    });
    _defineProperty(this, "saveContainerRef", ref => this.containerRef = ref);
    _defineProperty(this, "handleKeyboardLeftClick", () => {
      if (this.isLeftArrowVisible()) {
        this.handleLeftArrowClick(true);
      }
    });
    _defineProperty(this, "handleKeyboardRightClick", () => {
      if (this.isRightArrowVisible()) {
        this.handleRightArrowClick(true);
      }
    });
    _defineProperty(this, "handleLeftArrowClick", (isSourceKeyboard // TODO fix
    ) => this.props.isRTL ? this.goToNextPage(isSourceKeyboard) : this.goToPrevPage(isSourceKeyboard));
    _defineProperty(this, "handleRightArrowClick", (isSourceKeyboard // TODO fix
    ) => this.props.isRTL ? this.goToPrevPage(isSourceKeyboard) : this.goToNextPage(isSourceKeyboard));
    _defineProperty(this, "getNextPageIndex", shiftCount => {
      const {
        itemsCount,
        inFrameItemsCount
      } = this.props;
      return clamp(this.state.firstItemAtFrameIndex + shiftCount, 0, itemsCount - inFrameItemsCount);
    });
    _defineProperty(this, "goToPrevPage", isSourceKeyboard => {
      const {
        onPageChange,
        itemsPerPageCount
      } = this.props;
      if (this.state.isLastItemForcedInView) {
        this.setLastItemForcedInView(false);
        return;
      }
      this.updateState({
        firstItemAtFrameIndex: this.getNextPageIndex(-itemsPerPageCount),
        isLastItemForcedInView: false
      });
      onPageChange();
      if (isSourceKeyboard) {
        return;
      }
      this.focusContainer();
    });
    _defineProperty(this, "goToNextPage", isSourceKeyboard => {
      const {
        onPageChange,
        itemsPerPageCount,
        withDisabledNav
      } = this.props;
      if (withDisabledNav) {
        return;
      }
      const nextFirstItemAtFrameIndex = this.getNextPageIndex(itemsPerPageCount);
      this.updateState({
        firstItemAtFrameIndex: nextFirstItemAtFrameIndex,
        isLastItemForcedInView: nextFirstItemAtFrameIndex === this.state.firstItemAtFrameIndex
      });
      onPageChange();
      if (isSourceKeyboard) {
        return;
      }
      this.focusContainer();
    });
    _defineProperty(this, "checkItemVisible", itemIndex => {
      const {
        inFrameItemsCount
      } = this.props;
      const {
        firstItemAtFrameIndex
      } = this.state;
      return itemIndex >= firstItemAtFrameIndex && itemIndex < firstItemAtFrameIndex + inFrameItemsCount;
    });
    _defineProperty(this, "renderItem", itemId => {
      const {
        horizontalSpacing,
        loaderTheme,
        thumbnailHeight
      } = this.props;
      return /*#__PURE__*/React.createElement("div", {
        key: itemId,
        className: styles.item,
        style: {
          margin: "0 " + round(horizontalSpacing / 2) + "px"
        }
      }, startsWith(itemId, FAKE_ITEM_ID_PREFIX) ? /*#__PURE__*/React.createElement(ItemLoading, {
        style: {
          height: thumbnailHeight
        },
        active: !this.state.isTransitioning,
        theme: loaderTheme
      }) : this.renderItemWithOverlay(itemId));
    });
    _defineProperty(this, "saveActiveItemRef", ref => {
      this.activeItemRef = ref;
    });
    _defineProperty(this, "isActiveItem", itemId => indexOf(this.props.itemIds, itemId) === this.props.mainItemIndex);
    _defineProperty(this, "isActiveItemOutOfFrame", () => {
      const {
        mainItemIndex,
        inFrameItemsCount
      } = this.props;
      const {
        firstItemAtFrameIndex
      } = this.state;
      const bounds = [firstItemAtFrameIndex, firstItemAtFrameIndex + inFrameItemsCount - 1];
      return mainItemIndex < bounds[0] || mainItemIndex > bounds[1];
    });
    _defineProperty(this, "handleTransitionEnd", () => {
      this.setState({
        isTransitioning: false
      });
      this.props.onTransitionEnd();
    });
    _defineProperty(this, "saveActiveItemOverlayRef", ref => {
      this.activeItemOverlayRef = ref;
    });
    this.state = {
      firstItemAtFrameIndex: 0,
      isLastItemForcedInView: false,
      isTransitioning: false,
      isAnimationDisabled: false,
      mounted: false
    };
  }
  componentDidMount() {
    this.setState({
      mounted: true
    });
  }
  UNSAFE_componentWillReceiveProps(_ref) {
    let {
      reset,
      itemsCount,
      mainItemIndex
    } = _ref;
    if (reset && this.state.firstItemAtFrameIndex) {
      // TODO: looks silly...
      this.setState({
        firstItemAtFrameIndex: 0,
        isLastItemForcedInView: false
      });
      return;
    }
    if (mainItemIndex >= 0 && this.activeItemRef && this.activeItemOverlayRef) {
      if (!this.activeItemRef.contains(this.activeItemOverlayRef)) {
        this.activeItemRef.appendChild(this.activeItemOverlayRef);
      }
    }
    this.handleItemsCountReduced(itemsCount);
  }
  componentDidUpdate() {
    const missingItemsCount = this.getMissingItemsCount();
    if (missingItemsCount && this.props.canLoadMoreItems) {
      this.loadMore(missingItemsCount);
    }
    if (this.isActiveItemOutOfFrame()) {
      this.props.onActiveItemLeftFrame();
    }
  }
  handleItemsCountReduced(itemsCount) {
    const difference = this.props.itemsCount - itemsCount;
    if (difference > 0 && this.isLastPage()) {
      this.setState({
        firstItemAtFrameIndex: Math.max(0, this.state.firstItemAtFrameIndex - difference),
        isAnimationDisabled: true
      }, () => {
        this.setState({
          isAnimationDisabled: false
        });
      });
    }
  }
  renderItemWithOverlay(itemId) {
    const {
      renderItem
    } = this.props;
    const isActive = this.isActiveItem(itemId);
    return /*#__PURE__*/React.createElement("div", isActive && {
      ref: this.saveActiveItemRef
    }, renderItem(itemId, this.checkItemVisible));
  }
  shouldRenderShadyArrow() {
    const {
      withArrowsOutside,
      withShadyNavigation
    } = this.props;
    return !withArrowsOutside && withShadyNavigation;
  }
  getCustomNavArrow() {
    return /*#__PURE__*/React.createElement("div", {
      className: classnames(styles.shadyNavButton, this.props.shadyNavButtonClassName)
    }, /*#__PURE__*/React.createElement(InlineIcon, {
      icon: /*#__PURE__*/React.createElement(ArrowLeftShadyIcon, null)
    }));
  }
  renderActiveItemOverlay() {
    return /*#__PURE__*/React.cloneElement(this.props.activeItemOverlay, {
      ref: this.saveActiveItemOverlayRef
    });
  }
  render() {
    const {
      width,
      itemWidth,
      itemWidthWithSpacing,
      className,
      thumbnailHeight,
      navButtonClassName,
      navButtonStyle,
      mainItemIndex,
      itemsPerPageCount,
      preloadedPagesCount,
      accessibilityLabels,
      horizontalSpacing,
      withArrowsOutside,
      withNavAlwaysVisible,
      inFrameItemsCount,
      isConnectedToMainItemIndex,
      withKeyboardNavigation,
      withShadyNavigation,
      virtualPages,
      activeItemOverlay,
      isRTL,
      containerStyle
    } = this.props;
    const {
      firstItemAtFrameIndex,
      isLastItemForcedInView,
      isAnimationDisabled
    } = this.state;
    const {
      prevButtonAriaLabel,
      nextButtonAriaLabel
    } = accessibilityLabels;
    const containerClassNames = classnames(styles.container, className, {
      [styles['has-hover-gradient']]: !withArrowsOutside && !withShadyNavigation,
      [styles['has-shady-arrows']]: withShadyNavigation && !withArrowsOutside,
      [styles['has-always-visible-nav']]: withNavAlwaysVisible,
      [styles['has-nav-prev']]: this.isLeftArrowVisible(),
      [styles['has-nav-next']]: this.isRightArrowVisible(),
      [styles.rtl]: isRTL
    });
    const gradientNavOverlayHeight = {
      height: thumbnailHeight + "px !important"
    };
    const pseudoHeightClassName = cxs({
      '::after': gradientNavOverlayHeight,
      '::before': gradientNavOverlayHeight
    });
    const prerenderItemsCount = inFrameItemsCount + itemsPerPageCount * preloadedPagesCount;
    const preRenderedCount = typeof window === 'undefined' ? Math.max(DEFAULT_VIDEOS_COUNT, prerenderItemsCount) : prerenderItemsCount;
    const customNavArrow = this.getCustomNavArrow();
    return /*#__PURE__*/React.createElement(SliderWrapper, {
      style: containerStyle,
      ariaLabel: accessibilityLabels.containerAriaLabel,
      itemWidth: itemWidth,
      className: classnames(containerClassNames, {
        [pseudoHeightClassName]: thumbnailHeight
      }),
      onRef: this.saveContainerRef,
      isRTL: isRTL
    }, withKeyboardNavigation && /*#__PURE__*/React.createElement(KeyboardNavigation, {
      onKeyboardArrowLeft: this.handleKeyboardLeftClick,
      onKeyboardArrowRight: this.handleKeyboardRightClick
    }), isConnectedToMainItemIndex && /*#__PURE__*/React.createElement(MainItemWatcher, {
      firstItemAtFrameIndex: firstItemAtFrameIndex,
      inFrameItemsCount: inFrameItemsCount,
      mainItemIndex: mainItemIndex,
      onFirstItemIndexUpdate: this.setFirstItemAtFrameIndex,
      onForceLastItemUpdate: this.setLastItemForcedInView
    }), /*#__PURE__*/React.createElement("div", {
      className: navButtonClassName,
      style: navButtonStyle
    }, this.isLeftArrowVisible() && /*#__PURE__*/React.createElement(NavButton, _extends({
      className: styles.prev,
      style: {
        height: thumbnailHeight
      }
    }, this.shouldRenderShadyArrow() && {
      arrowIcon: customNavArrow
    }, {
      ariaLabel: isRTL ? nextButtonAriaLabel : prevButtonAriaLabel,
      dataHook: isRTL ? 'next-button' : 'prev-button',
      onClick: this.handleLeftArrowClick
    }))), /*#__PURE__*/React.createElement(ContentSlider, {
      dataHook: "content-slider",
      disabled: isAnimationDisabled,
      onTransitionEnd: this.handleTransitionEnd,
      width: itemWidthWithSpacing,
      currentPageNumber: firstItemAtFrameIndex,
      isRTL: isRTL
    }, /*#__PURE__*/React.createElement(ContentShifter, {
      width: width,
      reduceMarginsBy: round(horizontalSpacing / 2),
      itemWidth: itemWidthWithSpacing,
      inFrameItemsCount: inFrameItemsCount,
      active: isLastItemForcedInView,
      isRTL: isRTL
    }, /*#__PURE__*/React.createElement(PageList, {
      pageWidth: itemWidthWithSpacing,
      preRenderedCount: preRenderedCount,
      currentPageNumber: firstItemAtFrameIndex,
      itemsByPageNumber: virtualPages,
      renderItem: this.renderItem
    }))), /*#__PURE__*/React.createElement("div", {
      className: navButtonClassName,
      style: navButtonStyle
    }, this.isRightArrowVisible() && /*#__PURE__*/React.createElement(NavButton, _extends({
      className: styles.next,
      style: {
        height: thumbnailHeight
      },
      ariaLabel: isRTL ? prevButtonAriaLabel : nextButtonAriaLabel
    }, this.shouldRenderShadyArrow() && {
      arrowIcon: customNavArrow
    }, {
      dataHook: isRTL ? 'prev-button' : 'next-button',
      onClick: this.handleRightArrowClick
    }))), activeItemOverlay && this.renderActiveItemOverlay());
  }
}
_defineProperty(SliderComponent, "defaultProps", {
  onPageChange: noop,
  onTransitionEnd: noop,
  onActiveItemLeftFrame: noop
});
export const Slider = props => {
  const {
    width,
    itemWidth,
    horizontalSpacing,
    itemIds,
    itemsCount,
    mainItemIndex
  } = props;
  const itemWidthWithSpacing = itemWidth + horizontalSpacing;
  const inFrameItemsCount = Math.ceil(width / itemWidthWithSpacing);
  const calculatedProps = {
    ...props,
    inFrameItemsCount,
    itemWidthWithSpacing,
    loadedItemsCount: size(itemIds),
    isConnectedToMainItemIndex: !isUndefined(mainItemIndex),
    virtualPages: chunk(assign(map(range(0, itemsCount), value => FAKE_ITEM_ID_PREFIX + "-" + value), itemIds), 1)
  };
  return /*#__PURE__*/React.createElement(SliderComponent, calculatedProps);
};
Slider.defaultProps = {
  itemsPerPageCount: 3,
  preloadedPagesCount: 2,
  horizontalSpacing: 0,
  withArrowsOutside: false,
  withNavAlwaysVisible: false,
  withDisabledNav: false,
  withKeyboardNavigation: false,
  withShadyNavigation: false
};
//# sourceMappingURL=slider.js.map